// Servicio para manejar las operaciones relacionadas con los precios de venta

import db from "../db/database";
import { getVATByPostingGroups } from "./grupoIVAService";
import { aRegistroLocal } from "./identidadDePrecio";
import { refrescarPreciosEnSegundoPlano, syncSalesPrices } from "./salesPriceSyncService";
import { COUNTRY_CODES, PriceSource } from "../constants";

// Función para guardar precios de venta en la base de datos local
export const saveSalesPricesToLocalDB = async (salesPrices) => {
  try {
    const ahora = new Date();
    const pricesToAdd = salesPrices.map((price) => aRegistroLocal(price, ahora));

    await db.salesPrices.bulkAdd(pricesToAdd);
  } catch (error) {
    console.error("Error al guardar precios de venta en la base de datos local:", error);
    throw error;
  }
};

// Vista previa rápida (los primeros `limit`, solo datos locales, sin
// disparar sincronización con BC) para pintar la pantalla al instante
// mientras `getAllSalesPrices` hace su carga/sincronización completa por
// atrás. Dexie corta el recorrido de la tabla apenas junta `limit`
// coincidencias, así que es rápida sin importar el tamaño total de la tabla.
export const getSalesPricesPreview = async (limit = 50) => {
  try {
    const prices = await db.salesPrices
      .filter((price) => !price.deleted)
      .limit(limit)
      .toArray();
    return prices;
  } catch (error) {
    console.error("Error al obtener vista previa de precios de venta:", error);
    throw error;
  }
};

// Precios de venta locales, sin disparar sincronización con BC -- para
// pintar el listado sin duplicar la sincronización que ya maneja el botón
// "Sincronizar precios de venta" (syncSalesPrices) o el proceso general.
export const getSalesPricesLocal = async () => {
  try {
    return await db.salesPrices.filter((price) => !price.deleted).toArray();
  } catch (error) {
    console.error("Error al obtener precios de venta locales:", error);
    throw error;
  }
};

// Todos los precios locales, después de pedirle a Business Central los cambios.
//
// La lectura la hace syncSalesPrices, que no deja correr dos a la vez (tarea T2
// del acuerdo de la sincronización incremental). Esta función queda para la
// sincronización general, que espera la lista.
export const getAllSalesPrices = async () => {
  await syncSalesPrices();
  return db.salesPrices.filter((price) => !price.deleted).toArray();
};

// Precios de un producto, de la base local.
//
// No espera a Business Central (tarea T2 del acuerdo de la sincronización
// incremental). Antes cada línea de factura esperaba una lectura de red antes de
// tener precio: con una lectura grande --18 MB en Ecuador-- o con un relleno de
// contadores, la caja se quedaba parada a media venta. Ahora la línea usa lo que
// hay en la caja, y los cambios se piden por detrás sin esperar, como mucho cada
// cinco minutos.
export const getSalesPriceByItemNo = async (itemNo) => {
  try {
    // No espera a Business Central: los cambios se piden por detrás (ver
    // getSalesPriceByItemNo).
    refrescarPreciosEnSegundoPlano();

    // Buscar en la base de datos local
    const filteredPrices = await db.salesPrices
      .where("Item_No")
      .equals(itemNo)
      .and((price) => !price.deleted)
      .toArray();

    return filteredPrices;
  } catch (error) {
    console.error(`Error al obtener precio de venta para item ${itemNo}:`, error);
    throw error;
  }
};

// Obtener precios de venta por tipo de venta desde la base de datos local
export const getSalesPricesBySalesType = async (salesType) => {
  try {
    // No espera a Business Central: los cambios se piden por detrás (ver
    // getSalesPriceByItemNo).
    refrescarPreciosEnSegundoPlano();

    // Buscar en la base de datos local
    const filteredPrices = await db.salesPrices
      .where("Sales_Type")
      .equals(salesType)
      .and((price) => !price.deleted)
      .toArray();

    return filteredPrices;
  } catch (error) {
    console.error(`Error al obtener precios de venta para tipo ${salesType}:`, error);
    throw error;
  }
};

// Obtener precios de venta por código de moneda desde la base de datos local
export const getSalesPricesByCurrency = async (currencyCode) => {
  try {
    // No espera a Business Central: los cambios se piden por detrás (ver
    // getSalesPriceByItemNo).
    refrescarPreciosEnSegundoPlano();

    // Buscar en la base de datos local
    const filteredPrices = await db.salesPrices
      .where("Currency_Code")
      .equals(currencyCode)
      .and((price) => !price.deleted)
      .toArray();

    return filteredPrices;
  } catch (error) {
    console.error(`Error al obtener precios de venta para moneda ${currencyCode}:`, error);
    throw error;
  }
};

//Reglas para obtener precio de ventas
const getPriceEcuador = async (allPrices, itemNo, clientNo, customerPriceGroup, colegioNo) => {
  if (!allPrices || allPrices.length === 0) {
    console.warn(`No se encontraron precios para el producto ${itemNo}`);
    return null;
  }

  // Orden de prioridades :
  // 1. Colegio
  // 2. Cliente
  // 3. Grupo precio cliente
  // 4. Todos los clientes

  // Prioridad 1: Buscar precio por colegio
  if (colegioNo) {
    const colegioPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Colegio" || price.Sales_Type === "School") &&
        price.Sales_Code === colegioNo
    );
    if (colegioPrice) {
      return { ...colegioPrice, priceSource: PriceSource.SCHOOL };
    }
  }

  // Prioridad 2: Buscar precio por cliente específico
  if (clientNo) {
    const clientPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Cliente" || price.Sales_Type === "Customer") &&
        price.Sales_Code === clientNo
    );
    if (clientPrice) {
      return { ...clientPrice, priceSource: PriceSource.CLIENT };
    }
  }

  // Prioridad 3: Buscar precio por grupo de precio del cliente
  if (customerPriceGroup) {
    const groupPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Grupo precio cliente" ||
          price.Sales_Type === "Customer Price Group") &&
        price.Sales_Code === customerPriceGroup
    );
    if (groupPrice) {
      return { ...groupPrice, priceSource: PriceSource.CUSTOMER_PRICE_GROUP };
    }
  }

  // Prioridad 4: Buscar precio para todos los clientes
  const allClientsPrice = allPrices.find(
    (price) =>
      price.Sales_Type === "Todos los clientes" ||
      price.Sales_Type === "All Customers" ||
      !price.Sales_Type ||
      price.Sales_Type === ""
  );
  if (allClientsPrice) {
    return { ...allClientsPrice, priceSource: PriceSource.ALL_CUSTOMERS };
  }

  // Si no se encuentra ningún precio con las prioridades, devolver el primer precio válido
  console.warn(`No se encontró precio específico para producto ${itemNo}, usando precio genérico`);
  return { ...allPrices[0], priceSource: PriceSource.FALLBACK };
};

const getPriceParaguay = async (allPrices, product, clientNo, customerPriceGroup) => {
  if (clientNo) {
    const clientPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Cliente" || price.Sales_Type === "Customer") &&
        price.Sales_Code === clientNo
    );
    if (clientPrice) {
      return { ...clientPrice, priceSource: PriceSource.CLIENT };
    }
  }

  if (customerPriceGroup) {
    const groupPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Grupo precio cliente" ||
          price.Sales_Type === "Customer Price Group") &&
        price.Sales_Code === customerPriceGroup
    );
    if (groupPrice) {
      return { ...groupPrice, priceSource: PriceSource.CUSTOMER_PRICE_GROUP };
    }
  }

  const allClientsPrice = allPrices.find(
    (price) =>
      price.Sales_Type === "Todos los clientes" ||
      price.Sales_Type === "All Customers" ||
      !price.Sales_Type ||
      price.Sales_Type === ""
  );

  if (allClientsPrice) {
    return { ...allClientsPrice, priceSource: PriceSource.ALL_CUSTOMERS };
  }

  return { ...product, priceSource: PriceSource.PRODUCT };
};

const getPricePeru = async (
  allPrices,
  product,
  clientNo,
  customerPriceGroup,
  colegioNo,
  campaignNo
) => {
  if (colegioNo) {
    const colegioPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Contacto" || price.Sales_Type === "Contact") &&
        price.Sales_Code === colegioNo
    );
    if (colegioPrice) {
      return { ...colegioPrice, priceSource: PriceSource.SCHOOL };
    }
  }

  if (clientNo) {
    const clientPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Cliente" || price.Sales_Type === "Customer") &&
        price.Sales_Code === clientNo
    );
    if (clientPrice) {
      return { ...clientPrice, priceSource: PriceSource.CLIENT };
    }
  }

  if (customerPriceGroup) {
    const groupPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Grupo precio cliente" ||
          price.Sales_Type === "Customer Price Group") &&
        price.Sales_Code === customerPriceGroup
    );
    if (groupPrice) {
      return { ...groupPrice, priceSource: PriceSource.CUSTOMER_PRICE_GROUP };
    }
  }

  // Antes comparaba el código con el grupo de precio del cliente y buscaba
  // «Campain»: el precio de campaña sólo se encontraba si la campaña se llamaba
  // igual que el grupo, y nunca si Business Central lo mandaba en inglés.
  if (campaignNo) {
    const campaignPrice = allPrices.find(
      (price) =>
        (price.Sales_Type === "Campaña" ||
          price.Sales_Type === "Campaign" ||
          price.Sales_Type === "Campain") &&
        price.Sales_Code === campaignNo
    );
    if (campaignPrice) {
      return { ...campaignPrice, priceSource: PriceSource.CAMPAIGN };
    }
  }

  const allClientsPrice = allPrices.find(
    (price) =>
      price.Sales_Type === "Todos los clientes" ||
      price.Sales_Type === "All Customers" ||
      !price.Sales_Type ||
      price.Sales_Type === ""
  );

  if (allClientsPrice) {
    return { ...allClientsPrice, priceSource: PriceSource.ALL_CUSTOMERS };
  }

  return { ...product, priceSource: PriceSource.PRODUCT };
};

// Obtener precio de venta con prioridades por cliente, colegio, grupo y producto
export const getSalesPriceWithPriority = async (
  product,
  clientNo,
  customerPriceGroup,
  colegioNo,
  country,
  campaignNo = null
) => {
  try {
    // Obtener todos los precios de venta para el producto desde la base de datos local
    const allPrices = await getSalesPriceByItemNo(product.No);

    // La regla se elige por lista y no por una cadena de `||` encadenados.
    //
    // No es estética: con la cadena, dar de alta un país y olvidarse de este
    // archivo **no rompe nada visible** --se cae al `return null` de abajo y el
    // cajero lee «no hay precio configurado», que es mentira--. Le pasó a Puerto
    // Rico el 16/10/2026: el precio estaba en Business Central, el cliente tenía
    // su grupo, y aun así el producto no se podía agregar a la factura. La
    // prueba `todos los países del catálogo tienen regla de precios` cierra el
    // hueco para el siguiente.
    const CON_PRIORIDAD_ESTANDAR = [
      COUNTRY_CODES.ECUADOR,
      COUNTRY_CODES.HONDURAS,
      COUNTRY_CODES.ELSALVADOR,
      COUNTRY_CODES.COSTARICA,
      COUNTRY_CODES.PANAMA,
      // Puerto Rico resuelve por grupo de precio de cliente igual que los demás:
      // medido el 16/10/2026, 1.264 de sus 1.275 clientes tienen
      // `Customer_Price_Group` --«PVP 2026» en 1.262-- y los precios llegan como
      // `Sales_Type: "Customer Price Group"`.
      COUNTRY_CODES.PUERTORICO,
    ];

    if (CON_PRIORIDAD_ESTANDAR.includes(country?.code)) {
      return await getPriceEcuador(allPrices, product.No, clientNo, customerPriceGroup, colegioNo);
    }
    if (country?.code === COUNTRY_CODES.PERU) {
      return await getPricePeru(
        allPrices,
        product,
        clientNo,
        customerPriceGroup,
        colegioNo,
        campaignNo
      );
    }
    if (country?.code === COUNTRY_CODES.PARAGUAY) {
      return await getPriceParaguay(allPrices, product, clientNo, customerPriceGroup);
    }

    // Un país que no esté en ninguna de las dos ramas se quedaba sin precio y sin
    // explicación: quien llama sólo ve un valor vacío y le dice al cajero que el
    // producto no tiene precio configurado, que es falso. Se deja dicho cuál es la
    // causa real para que el siguiente país no tarde en descubrirse.
    console.warn(
      `No hay regla de precios para el país ${country?.code ?? "(sin país)"}: ` +
      `el producto ${product.No} se quedará sin precio`
    );
    return null;
  } catch (error) {
    console.error(`Error al obtener precio con prioridades para producto ${product.No}:`, error);
    throw error;
  }
};

/**
 * Obtiene el IVA aplicable según la combinación:
 * VAT_Prod_Posting_Group (producto) + VAT_Bus_Posting_Group (cliente)
 */
export const getVatByProductAndClient = async (item, client) => {
  if (!item) {
    return { VAT_Percent: 0 };
  }

  const normalizePostingGroup = (value) => (value ?? "").toString().trim().toUpperCase();
  const prodPostingGroup = normalizePostingGroup(item?.VAT_Prod_Posting_Group);
  const clientBusPostingGroup = normalizePostingGroup(client?.VAT_Bus_Posting_Group);

  if (!prodPostingGroup) {
    return { VAT_Percent: 0 };
  }

  const exactVat = await getVATByPostingGroups(prodPostingGroup, clientBusPostingGroup);
  if (exactVat && !exactVat.Blocked) {
    return exactVat;
  }

  const generalVat = await getVATByPostingGroups(prodPostingGroup, "");
  if (generalVat && !generalVat.Blocked) {
    return generalVat;
  }

  const candidates = await db.grupoIVA
    .filter(
      (row) =>
        !row.Blocked && normalizePostingGroup(row.VAT_Prod_Posting_Group) === prodPostingGroup
    )
    .toArray();

  if (candidates.length === 0) {
    return { VAT_Percent: 0 };
  }

  const bestCandidate = candidates.reduce((best, current) =>
    Number(current?.VAT_Percent ?? 0) > Number(best?.VAT_Percent ?? 0) ? current : best
  );

  return bestCandidate;
};
